import { includesOneOfErrorMessages, sanitizeObject, sanitizeUrl } from '@shared/utils'

const blacklist = ['password']

// Expected server responses that represent normal user-driven flow (failed credentials, form
// validation, expired codes, business-rule rejections, not-found navigation) rather than frontend
// defects. We match on the response *message* (substring, case-insensitive), not the HTTP status,
// because the backend is inconsistent with status codes — the message is the reliable signal.
// Anything whose message isn't listed here still reports, so a genuine bug that happens to surface as
// a 4xx is not hidden. Derived from a full triage of every prod AxiosError message (2026-05-29),
// extended after the 2026-07-13 triage.
//
// Intentionally kept VISIBLE (bug candidates, do NOT add): "Appointment is not in correct status"
// (double-submit), "Invalid Value for Param: sex_type" / ": mimetype" (controlled values →
// frontend/contract mismatch), "does not has permissions" (possible UI gating bug), and any 5xx /
// "HTML Response skipped" (real backend errors).
const EXPECTED_ERROR_MESSAGES = [
  // Auth — bad credentials / expired session / expired login code / rate limits
  'You are not authorized',
  'User not authenticated',
  'Email/password invalid',
  'DOB invalid or token invalid',
  'Code expired or invalid',
  'You have reached the maximum number of attempts',
  'Rate limit reached',
  'Your account has been locked due to too many failed login attempts',
  "Could not break the glass because password doesn't match",
  // Enrollment & verification
  'Email taken by enrolled user',
  'Verification code expired',
  'Verification code does not match',
  'The patient has not completed the enrollment process',
  // Resource no longer exists — renders a NotFound page
  'User not found',
  'User journey not found',
  'Provider not found',
  'Appointment not found',
  'Task not found',
  'Insurance not found',
  'Medication not found',
  'Cbo not found',
  'Conversation not found',
  // Scheduling / availability business rules
  'Patients cannot schedule an appointment within the next',
  'Cannot create new appointments for the user',
  'cannot be self scheduled',
  'taken by another provider', // appointment grabbed by another provider (race), on-demand or async
  'Max hours allowed per week exceeded',
  'Availability has appointments',
  'Availability is in the past',
  'Appointment is not in the future',
  'Date time range is overlapping', // with other availability / Admin Time / other admin time
  'An approved availability cannot be updated',
  'There are already async appointments ongoing',
  // Patient / clinical business rules
  'User already has active applications',
  'Cannot Re-engage this patient',
  'Patient must have assigned Provider',
  'Assessment cannot be completed because some required steps',
  "Gilead Application should be on 'Patient Signature' status",
  "Provider doesn't have access to that Treatment Type",
  'Cannot send messages to an archived conversation', // conversation archived while user was typing
  'Recipient is out of office',
  'Lab can only be updated when status is not Ready', // double-submit / stale lab view
  // Form / input validation
  'Phone is already taken',
  'Must be lower than Today',
  'SOAP content validation failed',
  'Abbreviation', // "Abbreviation <name> already taken" — snippet name collision
  "City can't be blank",
  'Please enter mandatory Rx BIN information',
  'Invalid Value for Param: zip', // user-typed zip in address lookup (sex_type/mimetype kept visible)
  'Patient Address field is too long', // DoseSpot rejects an over-long patient address
]

// Client-side failures that never received a response: the browser dropped/lost the connection
// (offline, backgrounded tab, flaky network), the request timed out, or it was aborted mid-flight.
// These are user-environment noise, not a defect — a reachable-but-failing server surfaces as 4xx/5xx,
// which still report. They carry no response body, so the message allowlist can't reach them; match on
// the axios error code/message instead.
function isNoResponseConnectivityError(error) {
  if (error.response) return false
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') return true
  const message = error.message || ''
  return message === 'Network Error' || message === 'Request aborted' || /timeout of \d+ms exceeded/.test(message)
}

export function reportAxiosError(error) {
  if (!window.honeybadger) return Promise.resolve(null)
  if (!error || !error.request || !error.config) return Promise.resolve(null)
  if (error.name === 'CanceledError') return Promise.resolve(null)

  // Best-effort/background requests (e.g. session heartbeats) opt out of reporting entirely;
  // they fail in volume on flaky or backgrounded connections and carry no actionable signal.
  if (error.config.skipReporting) return Promise.resolve(null)

  // Connectivity failures with no response (network drop / timeout / abort) — noise, not a defect.
  if (isNoResponseConnectivityError(error)) return Promise.resolve(null)

  // Don't report expected, user-driven responses — matched by message, not status (see above).
  if (includesOneOfErrorMessages(error, EXPECTED_ERROR_MESSAGES)) return Promise.resolve(null)

  const notification = mapAxiosErrorToNotificationObject(error)

  return new Promise((resolve) => {
    notification.afterNotify = (err, notice) => {
      if (!err && notice?.id) {
        resolve(`https://app.honeybadger.io/notice/${notice.id}`)
      } else {
        resolve(null)
      }
    }
    window.honeybadger.notify(error, notification)
  })
}

export function reportError(error) {
  if (!window.honeybadger) return
  window.honeybadger.notify(error, {
    afterNotify: (err, notice) => {
      if (!err && notice?.id) {
        try {
          error.honeybadgerUrl = `https://app.honeybadger.io/notice/${notice.id}`
        } catch {
          // Error object may be frozen/sealed (e.g. from React ErrorBoundary)
        }
      }
    },
  })
}

function mapAxiosErrorToNotificationObject({ name, message, response, config }) {
  const method = config.method?.toUpperCase()
  const endpoint = `${method} ${config.url}`
  const status = [response?.status, response?.statusText].join(' ')
  const isHtmlResponse = typeof response?.data === 'string' && response?.data.startsWith('<!DOCTYPE html>')

  let fingerprint = undefined
  try {
    const url = new URL(config.baseURL + config.url)
    fingerprint = sanitizeUrl(`${method} ${url.pathname}`)
  } catch {
    fingerprint = sanitizeUrl(endpoint)
  }

  let payload = undefined
  try {
    const parsedData = JSON.parse(config.data)
    payload = sanitizeObject(parsedData, blacklist)
  } catch {
    payload = config.data
  }

  return {
    name: name || 'AxiosError',
    message: `${fingerprint} -> ${response ? status : message}`,
    fingerprint,
    context: {
      name,
      status: response ? status : 'Response was not received',
      endpoint,
      message,
      payload,
      response: isHtmlResponse ? 'HTML Response skipped' : response?.data,
    },
  }
}
